import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SharableLinksIntro.css';
import {
  logSharableLinksIntroClosed,
  logSharableLinksIntroCtaClick,
  logSharableLinksIntroOpened,
  logSharableLinksIntroScrollDepth,
} from '../../services/analytics';

/** The cinematic "My Shareable Links" launch page, served from /public with its own assets. */
export const SHARABLE_LINKS_INTRO_URL = '/campaigns/sharable-links/index.html';
/** Banner winds up into the roller, then the page unrolls down the screen (and back). */
const BANNER_MS = 480;
const PAGE_OPEN_MS = 1150;
const PAGE_CLOSE_MS = 900;
/** How long the end of the page must stay in view before the curtain rolls itself back up. */
const AUTO_CLOSE_DELAY_MS = 900;
/** Room left under the curtain for the roller and its tab. */
const BOTTOM_GAP_PX = 30;

type Phase = 'closed' | 'opening' | 'open' | 'closing';
/** 'banner': the roller is winding the banner on or off; 'page': it is unrolling the page. */
type Stage = 'banner' | 'page';
type Segment = { to: number; ms: number; stage: Stage };
type OpenTrigger = 'banner' | 'roller' | 'auto';
type CloseTrigger = 'end_reached' | 'escape' | 'roller' | 'banner' | 'cta';
const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

type Props = {
  /** The page's "Create my first link" buttons open the create dialog instead of navigating. */
  onCreateLink: () => void;
  /** True while the curtain fully covers the tab, so the list behind it can be dropped. */
  onCoveredChange?: (covered: boolean) => void;
  /** Roll the page down on its own shortly after mount (deep link); `onAutoOpened` fires once it starts. */
  autoOpen?: boolean;
  onAutoOpened?: () => void;
};
/** Lets the tab settle (fonts, the banner's entrance) before the curtain falls. */
const AUTO_OPEN_DELAY_MS = 700;

// Slow start so each stage visibly rolls, then a soft landing.
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

function scrollParentOf(node: HTMLElement | null): HTMLElement {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/**
 * My Shareable Links header: a launch banner in the campaign's style. Tapping it winds the
 * banner up into its roller, which then unrolls the launch page as a curtain over the whole
 * tab. Reaching the end of the page (or Esc, or the roller's tab) rolls everything back.
 */
export function SharableLinksIntro({ onCreateLink, onCoveredChange, autoOpen = false, onAutoOpened }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLButtonElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const rollerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const geometry = useRef({ top: 0, bottom: 0, bannerBottom: 0, dockY: 0, insetLeft: 0, insetRight: 0 });
  const y = useRef(0);
  const animation = useRef(0);
  const lockedScroll = useRef<{ el: HTMLElement; overflow: string } | null>(null);
  const phaseRef = useRef<Phase>('closed');
  const [phase, setPhaseState] = useState<Phase>('closed');
  const [landed, setLanded] = useState(0);
  /** One reading session of the launch page, for analytics: when it opened and how far it was read. */
  const reading = useRef<{ openedAt: number; maxPercent: number; milestones: Set<number> } | null>(null);
  const setPhase = (next: Phase) => { phaseRef.current = next; setPhaseState(next); };

  /** `y` is the sheet's bottom edge on screen; everything else follows from it. */
  const paint = (nextY: number, stage: Stage) => {
    y.current = nextY;
    const { top, bannerBottom } = geometry.current;
    // Only the page stage unrolls the sheet; while the banner winds, the roller travels alone.
    if (sheetRef.current) sheetRef.current.style.height = stage === 'page' ? `${Math.max(0, nextY - top)}px` : '0px';
    if (rollerRef.current) {
      rollerRef.current.style.top = `${nextY - top - 6}px`;
      // Docked, the roller is banner-wide; it stretches to the full width as it rises over the tab bar.
      const { dockY, insetLeft, insetRight } = geometry.current;
      const reach = stage === 'page' ? 0 : Math.min(1, Math.max(0, (nextY - top) / Math.max(1, dockY - top)));
      rollerRef.current.style.left = `${insetLeft * reach}px`;
      rollerRef.current.style.right = `${insetRight * reach}px`;
      rollerRef.current.style.setProperty('--roll', `${nextY}px`);
    }
    const banner = bannerRef.current;
    if (!banner) return;
    if (stage === 'page') { banner.style.visibility = 'hidden'; return; }
    // The banner is wound into the roller: clip it at the roller's centre line.
    const hidden = Math.min(banner.offsetHeight, Math.max(0, bannerBottom - (nextY + 4)));
    banner.style.visibility = hidden >= banner.offsetHeight ? 'hidden' : '';
    banner.style.clipPath = `inset(0 0 ${hidden}px 0 round 28px)`;
  };

  const run = useCallback((segments: Segment[], next: Phase) => new Promise<void>((resolve) => {
    cancelAnimationFrame(animation.current);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;
    let from = y.current;
    let start = performance.now();
    const step = (now: number) => {
      const segment = segments[index];
      const t = reduce ? 1 : Math.min(1, (now - start) / segment.ms);
      paint(from + (segment.to - from) * easeInOut(t), segment.stage);
      if (t >= 1) {
        index += 1;
        if (index >= segments.length) { setPhase(next); setLanded((n) => n + 1); resolve(); return; }
        from = y.current;
        start = now;
      }
      animation.current = requestAnimationFrame(step);
    };
    animation.current = requestAnimationFrame(step);
  }), []);

  /** Pin the curtain layer over the app's whole scroll area (tab bar included), just under the app header. */
  const measure = () => {
    // While locked the scroller is overflow:hidden, so it is no longer found by its overflow.
    const scroller = lockedScroll.current?.el ?? scrollParentOf(rootRef.current);
    const isDocument = scroller === document.scrollingElement;
    const area = isDocument ? { top: 0, left: 0 } : scroller.getBoundingClientRect();
    const width = isDocument ? window.innerWidth : scroller.clientWidth; // clientWidth leaves its scrollbar out
    const banner = bannerRef.current?.getBoundingClientRect();
    const dock = dockRef.current?.getBoundingClientRect();
    const top = Math.max(0, Math.round(area.top));
    const bottom = window.innerHeight - BOTTOM_GAP_PX;
    geometry.current = {
      top, bottom, bannerBottom: banner?.bottom ?? top, dockY: (dock?.top ?? top) + 6,
      insetLeft: banner ? Math.max(0, banner.left - area.left - 6) : 0, // less the live roller's wider margin
      insetRight: banner ? Math.max(0, area.left + width - banner.right - 6) : 0,
    };
    const layer = layerRef.current;
    if (layer) {
      layer.style.top = `${top}px`;
      layer.style.left = `${area.left}px`;
      layer.style.width = `${width}px`;
    }
    if (frameRef.current) frameRef.current.style.height = `${bottom - top}px`;
    return scroller;
  };

  const lockScroll = (el: HTMLElement) => {
    if (lockedScroll.current) return;
    lockedScroll.current = { el, overflow: el.style.overflowY };
    el.style.overflowY = 'hidden';
  };
  const unlockScroll = () => {
    if (!lockedScroll.current) return;
    lockedScroll.current.el.style.overflowY = lockedScroll.current.overflow;
    lockedScroll.current = null;
  };

  const endReading = (trigger: CloseTrigger | 'unmount') => {
    const session = reading.current;
    if (!session) return;
    reading.current = null;
    logSharableLinksIntroClosed({ trigger, durationMs: Date.now() - session.openedAt, maxScrollPercent: session.maxPercent });
  };

  const open = useCallback(async (trigger: OpenTrigger) => {
    if (phaseRef.current !== 'closed') return;
    setPhase('opening');
    reading.current = { openedAt: Date.now(), maxPercent: 0, milestones: new Set() };
    logSharableLinksIntroOpened({ trigger });
    // Bring the banner to the top first, so the roller winds it up from where it is seen.
    const scroller = scrollParentOf(rootRef.current);
    const banner = bannerRef.current?.getBoundingClientRect();
    const scrollerTop = scroller === document.scrollingElement ? 0 : scroller.getBoundingClientRect().top;
    if (banner && (banner.top < scrollerTop || banner.top > scrollerTop + 140)) {
      scroller.scrollBy({ top: banner.top - scrollerTop - 12, behavior: 'smooth' });
      await wait(420);
    }
    if ((phaseRef.current as Phase) !== 'opening') return;
    lockScroll(measure());
    const { top, bottom, dockY } = geometry.current;
    y.current = dockY;
    await run([{ to: top, ms: BANNER_MS, stage: 'banner' }, { to: bottom, ms: PAGE_OPEN_MS, stage: 'page' }], 'open');
    onCoveredChange?.(true);
  }, [onCoveredChange, run]);

  const close = useCallback(async (trigger: CloseTrigger) => {
    if (phaseRef.current !== 'open' && phaseRef.current !== 'opening') return;
    endReading(trigger);
    cancelAnimationFrame(animation.current);
    setPhase('closing');
    onCoveredChange?.(false);
    await nextFrame(); // let the list return, so the banner's resting place is measured with it
    const fromY = y.current;
    measure();
    y.current = fromY;
    const { top, dockY } = geometry.current;
    await run([{ to: top, ms: PAGE_CLOSE_MS, stage: 'page' }, { to: dockY, ms: BANNER_MS, stage: 'banner' }], 'closed');
    unlockScroll();
    if (bannerRef.current) { bannerRef.current.style.clipPath = ''; bannerRef.current.style.visibility = ''; }
  }, [onCoveredChange, run]);
  const toggle = (trigger: 'banner' | 'roller') => (phaseRef.current === 'closed' ? void open(trigger) : void close(trigger));

  // Wire the page's buttons to the app, and roll up once its end has been reached.
  const handleFrameLoad = useCallback(() => {
    const win = frameRef.current?.contentWindow;
    const doc = frameRef.current?.contentDocument;
    if (!win || !doc) return;
    // The curtain is the frame: the page's own scrollbar would draw a bright strip down its edge.
    const style = doc.createElement('style');
    style.textContent = 'html{scrollbar-width:none}html::-webkit-scrollbar{display:none}';
    doc.head.appendChild(style);
    doc.addEventListener('click', (event) => {
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor || anchor.getAttribute('href')?.startsWith('#')) return;
      event.preventDefault();
      const label = (anchor.textContent || '').trim();
      if (anchor.href.includes('htns.sg/super-picker')) {
        const creates = /create/i.test(label);
        logSharableLinksIntroCtaClick({ label, action: creates ? 'create_link' : 'close' });
        void close('cta');
        if (creates) window.setTimeout(onCreateLink, PAGE_CLOSE_MS + BANNER_MS);
      } else {
        logSharableLinksIntroCtaClick({ label: label || anchor.href, action: 'external' });
        window.open(anchor.href, '_blank', 'noopener');
      }
    });
    doc.addEventListener('keydown', (event) => { if (event.key === 'Escape') void close('escape'); });
    let timer: number | undefined;
    win.addEventListener('scroll', () => {
      window.clearTimeout(timer);
      const scrollHeight = doc.documentElement.scrollHeight;
      const atEnd = win.scrollY + win.innerHeight >= scrollHeight - 4;
      const session = reading.current;
      if (session) {
        const percent = atEnd ? 100 : Math.min(99, Math.round(((win.scrollY + win.innerHeight) / Math.max(1, scrollHeight)) * 100));
        session.maxPercent = Math.max(session.maxPercent, percent);
        SCROLL_MILESTONES.forEach((milestone) => {
          if (percent >= milestone && !session.milestones.has(milestone)) {
            session.milestones.add(milestone);
            logSharableLinksIntroScrollDepth({ percent: milestone });
          }
        });
      }
      if (atEnd && phaseRef.current === 'open') timer = window.setTimeout(() => void close('end_reached'), AUTO_CLOSE_DELAY_MS);
    }, { passive: true });
  }, [close, onCreateLink]);

  // Keep an open curtain fitted to the screen; Esc rolls it up.
  useEffect(() => {
    if (phase !== 'open') return;
    const fit = () => { measure(); paint(geometry.current.bottom, 'page'); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') void close('escape'); };
    window.addEventListener('resize', fit);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('keydown', onKey); };
  }, [phase, close]);

  // The parent passes a fresh callback each render; reading it through a ref keeps the timer from restarting.
  const onAutoOpenedRef = useRef(onAutoOpened);
  onAutoOpenedRef.current = onAutoOpened;
  useEffect(() => {
    if (!autoOpen) return;
    const timer = window.setTimeout(() => { onAutoOpenedRef.current?.(); void open('auto'); }, AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [autoOpen, open]);

  // Leaving the tab with the page still down ends that reading session too.
  useEffect(() => () => { cancelAnimationFrame(animation.current); unlockScroll(); endReading('unmount'); }, []);

  const expanded = phase === 'open' || phase === 'opening';
  const bump = landed ? `sli-roller--landed-${landed % 2}` : '';
  return (
    <div ref={rootRef} className={`sli sli--${phase}`}>
      <button ref={bannerRef} type="button" className="sli-banner" onClick={() => toggle('banner')} aria-expanded={expanded} aria-controls="sli-sheet">
        <span className="sli-blob sli-blob--one" aria-hidden="true" />
        <span className="sli-blob sli-blob--two" aria-hidden="true" />
        <span className="sli-sparkles" aria-hidden="true">{Array.from({ length: 9 }, (_, i) => <i key={i} style={{ '--i': i, '--t': `${14 + ((i * 37) % 70)}%` } as React.CSSProperties} />)}</span>
        <span className="sli-copy">
          <span className="sli-topline">
            <span className="sli-kicker">Introducing</span>
            <span className="sli-pill">✨ Just launched</span>
          </span>
          <span className="sli-title">
            <span className="sli-my">My</span> Shareable <mark>Links</mark> <span className="sli-link" aria-hidden="true">🔗</span>
          </span>
          <span className="sli-tagline">Send a 3D map. Get a <mark>real lead.</mark></span>
          <span className="sli-sub">
            Send a link to your buyers. They confirm their mobile number on WhatsApp to open it, and each one is
            saved to your Super Connector as a lead.
          </span>
        </span>
        <span className="sli-phone" aria-hidden="true">
          <span className="sli-phone-notch" />
          <span className="sli-chat">
            <span className="sli-chat-head"><b>B</b>Buyer · WhatsApp</span>
            <span className="sli-bubble">Here’s the 3D map of the launch — spin the site &amp; check the sunlight 🌤️<u>htns.sg/xY7k2</u></span>
            <span className="sli-toast"><b>✓</b><span><em>New lead · just now</em>Verified → saved to Super Connector</span></span>
          </span>
        </span>
        <span className="sli-cta">See how it works <span className="sli-cta-arrow" aria-hidden="true">↓</span></span>
      </button>

      {/* At rest the roller is docked under the banner. */}
      <div className="sli-dock" aria-hidden="true">
        <div ref={dockRef} className={`sli-roller ${phase === 'closed' ? bump : ''}`} onClick={() => toggle('roller')}>
          <span className="sli-roller-tab">Pull to read</span>
        </div>
      </div>

      {/* In motion and open, a fixed layer over the tab carries the roller and the page. */}
      <div ref={layerRef} className="sli-layer" role="dialog" aria-label="Introducing My Shareable Links" aria-hidden={!expanded}>
        <div className="sli-sheet" id="sli-sheet" ref={sheetRef}>
          <span className="sli-sheet-loading" aria-hidden="true">Huttons Super Picker presents</span>
          {/* Loaded once the curtain has landed: starting the page's 3D scene mid-roll stalls the animation. */}
          {(phase === 'open' || phase === 'closing') && (
            <iframe ref={frameRef} src={SHARABLE_LINKS_INTRO_URL} title="Introducing My Shareable Links"
              onLoad={handleFrameLoad} style={{ height: geometry.current.bottom - geometry.current.top || undefined }} />
          )}
          <span className="sli-curl" aria-hidden="true" />
        </div>
        <div ref={rollerRef} className={`sli-roller sli-roller--live ${phase === 'open' ? bump : ''}`} onClick={() => void close('roller')} aria-hidden="true">
          <span className="sli-roller-tab">Roll up ↑</span>
        </div>
      </div>
    </div>
  );
}
